import { Box, Typography } from "@mui/material";
import { IdentityTag } from "../../../polish/IdentityTag";
import { toneTextColour } from "../../../polish/LabelValueRow";
import { TABULAR, type TonalTokens } from "../../../polish/tokens";
import { signedPercent, signTone, type NoteRow } from "./feedModel";

// A note's parts, shared by the report page's notes table and the hub
// card's note rows, so a note reads the same way in both.

export const EMPTY = "–";

export const cellSx = {
  ...TABULAR,
  fontSize: { xs: "0.8125rem", md: "0.875rem" },
  lineHeight: "20px",
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
} as const;

/** The report page's notes table: every part a step larger, the figures bolder. */
export const noteTextSx = (large: boolean) =>
  large
    ? ({
        ...cellSx,
        fontSize: { xs: "0.875rem", md: "0.9375rem" },
        lineHeight: "22px",
      } as const)
    : cellSx;

interface PartProps {
  row: NoteRow;
  tokens: TonalTokens;
  /** The report page's table rather than a hub card's rows. */
  large?: boolean;
}

export const Firm = ({ row, tokens, large = false }: PartProps) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
    <Typography
      component="span"
      data-testid="note-firm"
      sx={{ ...noteTextSx(large), fontWeight: 500, color: tokens.text }}
    >
      {row.firm}
    </Typography>
    {row.tickerTag !== null && <IdentityTag>{row.tickerTag}</IdentityTag>}
  </Box>
);

interface RatingProps {
  row: Pick<NoteRow, "rating" | "previousRating" | "ratingTone" | "initiated">;
  tokens: TonalTokens;
  large?: boolean;
}

/** "⇧ Hold → Buy" for a move, "Buy" kept, "Buy" with "Initiated" for a new one. */
export const Rating = ({ row, tokens, large = false }: RatingProps) => {
  const textSx = noteTextSx(large);
  if (row.rating === null)
    return (
      <Typography
        component="span"
        data-testid="note-rating"
        sx={{ ...textSx, color: tokens.faint }}
      >
        {EMPTY}
      </Typography>
    );
  const moved = row.ratingTone !== null;
  const colour = moved ? toneTextColour(row.ratingTone, tokens) : tokens.muted;
  const glyph = row.ratingTone === "gain" ? "⇧" : "⇩";
  const fullRating =
    row.previousRating === null
      ? row.rating
      : `${row.previousRating} → ${row.rating}`;
  // Flex parts, so a tight column shortens the old rating and never the new.
  const partSx = { ...textSx, flex: "none", overflow: "visible" } as const;
  // A step bolder in the table: the rating is what the note says.
  const weight = (moved ? 500 : 400) + (large ? 100 : 0);
  return (
    <Box
      data-testid="note-rating"
      sx={{ display: "flex", alignItems: "center", minWidth: 0 }}
      title={fullRating}
    >
      {moved && (
        <Typography
          component="span"
          data-testid="rating-glyph"
          sx={{ ...partSx, color: colour, mr: 0.5 }}
        >
          {glyph}
        </Typography>
      )}
      {row.previousRating !== null && (
        <>
          <Typography
            component="span"
            sx={{ ...textSx, flex: "0 1 auto", color: tokens.muted }}
          >
            {row.previousRating}
          </Typography>
          <Typography
            component="span"
            sx={{ ...partSx, color: tokens.faint, mx: 0.5 }}
          >
            →
          </Typography>
        </>
      )}
      <Typography
        component="span"
        sx={{
          ...textSx,
          flex: "0 1 auto",
          color: colour,
          fontWeight: weight,
        }}
      >
        {row.rating}
      </Typography>
      {row.initiated && (
        <Box component="span" sx={{ ml: 0.75, flex: "none" }}>
          <IdentityTag>Initiated</IdentityTag>
        </Box>
      )}
    </Box>
  );
};

interface TargetProps {
  row: Pick<NoteRow, "targetBefore" | "targetAfter" | "targetTone">;
  tokens: TonalTokens;
  large?: boolean;
}

/** The new target's colour: raised green, cut red, kept grey. */
export const targetColour = (
  row: TargetProps["row"],
  tokens: TonalTokens,
): string =>
  row.targetTone === "flat" || row.targetTone === null
    ? tokens.muted
    : toneTextColour(row.targetTone, tokens);

/** "$600 → $650": the new target in its move's colour, grey when kept. */
export const Target = ({ row, tokens, large = false }: TargetProps) => {
  const textSx = noteTextSx(large);
  if (row.targetAfter === null)
    return (
      <Typography
        component="span"
        data-testid="note-target"
        sx={{ ...textSx, color: tokens.faint, textAlign: "right" }}
      >
        {EMPTY}
      </Typography>
    );
  return (
    <Typography
      component="span"
      data-testid="note-target"
      sx={{
        ...textSx,
        color: tokens.muted,
        fontWeight: large ? 500 : 400,
        textAlign: "right",
      }}
    >
      {row.targetBefore !== null && (
        <>
          {row.targetBefore}
          <Box component="span" sx={{ color: tokens.faint, mx: 0.5 }}>
            →
          </Box>
        </>
      )}
      <Box
        component="span"
        data-testid="note-target-after"
        sx={{ color: targetColour(row, tokens), fontWeight: large ? 600 : 500 }}
      >
        {row.targetAfter}
      </Box>
    </Typography>
  );
};

/** "+8.3%"; a raise or cut without the old target shows its arrow alone. */
export const Change = ({ row, tokens, large = false }: PartProps) => {
  let text = "";
  let colour = tokens.muted;
  if (row.change !== null) {
    const tone = signTone(row.change);
    text = signedPercent(row.change);
    colour = tone === "flat" ? tokens.muted : toneTextColour(tone, tokens);
  }
  if (row.change === null && row.targetTone === "gain") {
    text = "↑";
    colour = toneTextColour("gain", tokens);
  }
  if (row.change === null && row.targetTone === "loss") {
    text = "↓";
    colour = toneTextColour("loss", tokens);
  }
  return (
    <Typography
      component="span"
      data-testid="note-change"
      sx={{
        ...noteTextSx(large),
        color: colour,
        fontWeight: large ? 600 : 500,
        textAlign: "right",
      }}
    >
      {text}
    </Typography>
  );
};
